import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { animated, useSpring, useSprings } from '@react-spring/web';
import React, { useState } from 'react';
import { useInView } from 'react-intersection-observer';
const PolybagsSection = () => {
  const [selectedPolybag, setSelectedPolybag] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const polybags = [
    {
      name: 'Eco-Friendly Polybags',
      description: 'Biodegradable bags made from sustainable materials.',
      detailedDescription: 'Made from biodegradable, eco-conscious materials to reduce environmental impact and support green initiatives.',
      image: 'https://media.istockphoto.com/id/1297109141/photo/garbage-bags.jpg?s=612x612&w=0&k=20&c=auBhJP8iEubnoiXgGGyYwbTgt6eXWRhyOomPvxPNmqo='
    },
    {
      name: 'Heavy-Duty Polybags',
      description: 'Durable bags for tough industrial use.',
      detailedDescription: 'Engineered for strength and resistance, these bags handle heavy loads and demanding environments with ease.',
      image: 'https://media.istockphoto.com/id/1496382965/photo/production-of-polypropylene-industrial-bags.jpg?s=612x612&w=0&k=20&c=D-8vYBt2V9jMAqa1taXQ1RwDKItDSXN8fxdTTjGl4Uw='
    },
    {
      name: 'Custom Branded Polybags',
      description: 'Personalized bags with your logo or design.',
      detailedDescription: 'Enhance brand visibility with custom-printed polybags tailored to your design, logo, or messaging.',
      image: '/custombranded.jpeg'
    },
    {
      name: 'Crystal Clear Polybags',
      description: 'Transparent bags for visible product display.',
      detailedDescription: 'High-clarity polybags that showcase your products while offering secure protection.',
      image: 'https://static.vecteezy.com/system/resources/previews/057/214/539/large_2x/a-tightly-wrapped-rectangular-object-in-clear-plastic-forms-an-abstract-design-of-lines-and-shapes-the-dark-background-enhances-the-plastic-s-transparency-free-photo.jpg'
    },
    {
      name: 'Food-Safe Polybags',
      description: 'Hygienic bags approved for food contact.',
      detailedDescription: 'Manufactured to food-grade standards for safe, clean, and contamination-free food packaging.',
      image: 'https://static.vecteezy.com/system/resources/previews/060/267/390/non_2x/a-person-hand-holding-a-bag-of-meat-in-their-hand-free-photo.jpeg'
    },
    {
      name: 'Industrial Polybags',
      description: 'Reliable bags for general industrial use.',
      detailedDescription: 'Versatile and resilient polybags designed for manufacturing, shipping, and warehouse needs.',
      image: 'https://media.gettyimages.com/id/1399020587/photo/manufacturers-engineer-and-tablet-working-at-plastic-bag-manufacturers.jpg?s=612x612&w=0&k=20&c=e2vq4rqpD_f9c1O8zkFcxsWkDLhwcIpE4jSmuCes-gE='
    },
    {
      name: 'Biohazard Containment Bags',
      description: 'Safe transport of hazardous biological materials.',
      detailedDescription: 'Compliant with medical standards for securely storing and transporting biohazardous waste or specimens.',
      image: 'https://static.vecteezy.com/system/resources/previews/005/134/491/non_2x/man-holding-a-biohazard-specimen-bag-in-his-hand-with-two-test-tubes-inside-free-photo.jpg'
    },
    {
      name: 'Textile & Garment Bags',
      description: 'Protective bags for clothing and fabrics.',
      detailedDescription: 'Designed to shield garments and textiles from dust and moisture during storage or shipping.',
      image: 'https://static.vecteezy.com/system/resources/previews/057/214/539/large_2x/a-tightly-wrapped-rectangular-object-in-clear-plastic-forms-an-abstract-design-of-lines-and-shapes-the-dark-background-enhances-the-plastic-s-transparency-free-photo.jpg'
    }
  ];
  

  const [ref, inView] = useInView({
    triggerOnce: true, // Animation triggers only once when section comes into view
    threshold: 0.1, // Percentage of the section visible before triggering
  });

  // Animation for polybag cards
  const springs = useSprings(
    polybags.length,
    polybags.map((_, index) => ({
      from: { opacity: 0, transform: 'translateY(20px)' },
      to: { opacity: inView ? 1 : 0, transform: inView ? 'translateY(0px)' : 'translateY(20px)' },
      delay: index * 100,
    }))
  );

  // Animation for modal
  const modalSpring = useSpring({
    opacity: isModalOpen ? 1 : 0,
    transform: isModalOpen ? 'scale(1)' : 'scale(0.9)',
    config: { tension: 250, friction: 20 },
  });

  const openModal = (polybag) => {
    setSelectedPolybag(polybag);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedPolybag(null);
    setIsModalOpen(false);
  };

  return (
    <section ref={ref} className="bg-background-primary py-16 px-4 md:px-8 lg:px-12">
    <div className="container mx-auto text-center">
      <h3 className="text-text-primary text-lg font-semibold mb-2">Our Polybags</h3>
      <h2 className="text-3xl font-bold mb-4 text-primary">Discover the Perfect <br /> Polybag for Your Needs</h2>
      <p className="text-text-primary mb-6">Explore a variety of polybags tailored to your specific needs.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {springs.map((springStyle, index) => (
          <animated.div
            key={index}
            style={springStyle}
            className="relative bg-background-primary shadow-lg rounded-md p-6 text-left hover:shadow-xl transition-shadow duration-300 hover:bg-secondary transform hover:scale-105 transition-transform"
          >
            <h3 className="text-lg font-semibold text-primary mb-2">{polybags[index].name}</h3>
            <p className="text-text-primary mb-4">{polybags[index].description}</p>
            <button
              onClick={() => openModal(polybags[index])}
              className="relative z-20 inline-flex items-center text-[#d11119] hover:text-red-700 font-medium transition-colors"
            >
              Learn more
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </button>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-primary opacity-0 hover:opacity-10 transition-opacity duration-500 pointer-events-none"></div>
          </animated.div>
        ))}
      </div>
    </div>
    {isModalOpen && selectedPolybag && (
      <animated.div style={modalSpring} className="fixed inset-0 z-50 flex items-center justify-center bg-background-secondary bg-opacity-50">
        <div className="bg-background-primary rounded-lg shadow-lg max-w-lg w-full p-4 relative">
          <button
            onClick={closeModal}
            className="absolute top-2 right-2 text-text-primary hover:text-gray-600 text-xl"
          >
            &times;
          </button>
          <div className="text-center">
            <img
              src={selectedPolybag.image}
              alt={selectedPolybag.name}
              className="w-full h-40 object-contain mb-4"
            />
            <h3 className="text-xl font-bold text-primary mb-2">{selectedPolybag.name}</h3>
            <p className="text-text-primary text-sm mb-4">{selectedPolybag.detailedDescription}</p>
          </div>
        </div>
      </animated.div>
    )}
  </section>
  );
};

export default PolybagsSection;